import React, { useEffect, useRef, useState } from "react";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
  LinkIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { currencyFormat } from "../helpers.js";
import {
  allocatedFormulaHint,
  calculateAllocatedRd,
  CONTRACTOR_CLAIM_RATE,
  EMPLOYEE_CLAIM_RATE,
  EMPLOYMENT_TYPES,
  salarySourceHint,
  suggestRdPctHint,
} from "../utils/companyTeamAllocation.js";
import { useCompanyTeamAllocation, buildLinkedInPeopleUrl } from "../hooks/useCompanyTeamAllocation.js";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "rd", label: "Likely R&D" },
  { id: "other", label: "Other" },
];

const INLINE_INPUT_CLASS =
  "w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors";

const CLICKABLE_CELL_CLASS =
  "w-full rounded-md px-2 py-1 text-left transition-colors hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-900";

const TABLE_HEAD_CLASS =
  "pb-3 px-2 text-center text-xs font-medium uppercase tracking-wide text-gray-500";

const HOVER_TOOLTIP_CLASS =
  "absolute right-0 top-full z-20 pt-1 opacity-0 invisible transition-all duration-150 group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible";

const REFRESH_TEAM_TOOLTIP =
  "Reload people from Airtable for this company's domain. Adds newly found matches without removing existing rows.";

const INDEX_TEAM_TOOLTIP =
  "Send your company LinkedIn page to Clay to discover team members and write them to Airtable. We auto-check for new people every 30 seconds for up to 10 minutes.";

const RE_ESTIMATE_TOOLTIP =
  "Estimate market salary and % R&D from titles and locations";

const SHARE_LINK_TOOLTIP =
  "Copy a read-only link to this R&D allocation for clients and prospects.";

const ESTIMATED_REFUND_RATE = 0.6;

const HoverTooltip = ({ tip, className = "", children }) => (
  <span className={`group relative inline-flex ${className}`}>
    {children}
    <span className={HOVER_TOOLTIP_CLASS} role="tooltip">
      <span className="block w-max max-w-xs rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm">
        <span className="text-xs leading-relaxed text-gray-600">{tip}</span>
      </span>
    </span>
  </span>
);

const UPGRADED_FALLBACK_LOGO = "/upgraded-collapsed-logo.png";

const initialsFromName = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const formatSalary = (salary) => {
  if (typeof salary !== "number" || Number.isNaN(salary)) return "—";
  return currencyFormat(salary);
};

const RdBadge = ({ isRd, onToggle }) => {
  const label = isRd ? "Likely R&D" : "Other";
  const className = [
    "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
    isRd ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600",
    onToggle
      ? isRd
        ? "cursor-pointer hover:bg-emerald-100"
        : "cursor-pointer hover:bg-gray-200"
      : "",
  ].join(" ");

  if (!onToggle) {
    return <span className={className}>{label}</span>;
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className={className}
      title={isRd ? "Mark as Other" : "Mark as Likely R&D"}
      aria-label={`${label}. Click to mark as ${isRd ? "Other" : "Likely R&D"}.`}
    >
      {label}
    </button>
  );
};

const StatCard = ({ label, value, hint, emphasize = false, className = "" }) => (
  <div
    className={[
      "rounded-xl border px-5 py-5",
      emphasize ? "border-gray-200 bg-gray-50" : "border-gray-100 bg-white",
      className,
    ].join(" ")}
  >
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p
      className={[
        "mt-2 font-semibold tabular-nums text-gray-900",
        emphasize ? "text-2xl sm:text-3xl" : "text-lg",
      ].join(" ")}
    >
      {value}
    </p>
    {hint ? <p className="mt-2 text-sm leading-relaxed text-gray-500">{hint}</p> : null}
  </div>
);

const TeamBreakdownRow = ({
  canadaCount,
  memberCount,
  employeeCount,
  contractorCount,
  rdCount,
  rdShare,
}) => {
  const items = [
    {
      label: "Employees",
      value: employeeCount,
      hint:
        contractorCount > 0
          ? `${contractorCount} contractor${contractorCount === 1 ? "" : "s"}`
          : "team members",
    },
    {
      label: "In Canada",
      value: canadaCount,
      hint: `of ${memberCount}`,
    },
    {
      label: "Likely R&D",
      value: rdCount,
      hint: `${Math.round(rdShare * 100)}% of team`,
    },
  ];

  return (
    <div className="flex items-stretch justify-center">
      {items.map((item, index) => (
        <div
          key={item.label}
          className={[
            "min-w-[6.5rem] px-4 py-0.5 text-center",
            index > 0 ? "border-l border-gray-100" : "",
          ].join(" ")}
        >
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</p>
          <p className="mt-0.5 text-lg font-semibold tabular-nums leading-none text-gray-900">
            {item.value}
          </p>
          <p className="mt-1 text-xs text-gray-500">{item.hint}</p>
        </div>
      ))}
    </div>
  );
};

const AllocationStats = ({
  allocationSummary,
  companyName,
  domain,
  logoUrl,
}) => {
  const formulaHint = `Employees ≤${Math.round(EMPLOYEE_CLAIM_RATE * 100)}% · contractors ≤${Math.round(
    CONTRACTOR_CLAIM_RATE * 100
  )}% × salary × % R&D`;
  const allocatedPct = Math.round((allocationSummary.allocatedShare || 0) * 100);
  const unallocatedPct = Math.max(0, 100 - allocatedPct);
  const estimatedRefund = Math.round((allocationSummary.allocatedTotal || 0) * ESTIMATED_REFUND_RATE);
  const refundPct = Math.round(ESTIMATED_REFUND_RATE * 100);
  const stats = [
    {
      label: "Total Salary",
      value: formatSalary(allocationSummary.salaryTotal),
      hint: "team payroll",
    },
    {
      label: "Unallocated Salary",
      value: formatSalary(allocationSummary.unallocatedTotal),
      hint: `${unallocatedPct}% of total`,
    },
    {
      label: "Allocated Salary",
      value: formatSalary(allocationSummary.allocatedTotal),
      hint: `${allocatedPct}% of total`,
      tip: formulaHint,
    },
    {
      label: "Estimated $ Refund",
      value: formatSalary(estimatedRefund),
      hint: `${refundPct}% of allocated`,
      emphasize: true,
    },
  ];

  return (
    <div>
      <div className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CompanyBrandMark
            companyName={companyName}
            domain={domain}
            logoUrl={logoUrl}
            compact
          />
          <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:w-auto sm:shrink-0 sm:items-end sm:justify-end sm:gap-6 lg:gap-8">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={[
                  "min-w-0 text-right",
                  stat.tip ? "group relative cursor-help" : "",
                ].join(" ")}
                tabIndex={stat.tip ? 0 : undefined}
                aria-label={`${stat.label}: ${stat.value}, ${stat.hint}${stat.tip ? `. ${stat.tip}` : ""}`}
              >
                <p
                  className={[
                    "flex items-center justify-end gap-1 font-medium leading-snug",
                    stat.emphasize ? "text-sm text-gray-900" : "text-xs text-gray-500",
                  ].join(" ")}
                >
                  {stat.label}
                  {stat.tip ? (
                    <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                  ) : null}
                </p>
                <p
                  className={[
                    "mt-0.5 leading-none tabular-nums text-gray-900",
                    stat.emphasize
                      ? "text-3xl font-bold"
                      : "text-lg font-semibold",
                  ].join(" ")}
                >
                  {stat.value}
                </p>
                <p
                  className={[
                    "mt-1 tabular-nums",
                    stat.emphasize ? "text-sm text-gray-500" : "text-xs text-gray-400",
                  ].join(" ")}
                >
                  {stat.hint}
                </p>
                {stat.tip ? (
                  <div className={HOVER_TOOLTIP_CLASS}>
                    <div className="w-max max-w-xs rounded-lg border border-gray-100 bg-white px-3 py-2 text-left shadow-sm">
                      <p className="text-xs leading-relaxed text-gray-600">{stat.tip}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamLoadingState = ({ compact = false } = {}) => (
  <div className="space-y-3">
    {compact ? (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="animate-pulse rounded-xl border border-gray-100 px-5 py-5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-gray-100" />
            <div className="space-y-1.5">
              <div className="h-3 w-28 rounded bg-gray-100" />
              <div className="h-2.5 w-20 rounded bg-gray-100" />
            </div>
          </div>
        </div>
        {[0, 1].map((item) => (
          <div key={item} className="animate-pulse rounded-xl border border-gray-100 px-5 py-5">
            <div className="h-3 w-28 rounded bg-gray-100" />
            <div className="mt-3 h-8 w-24 rounded bg-gray-100" />
            <div className="mt-2 h-3 w-32 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    ) : (
      <div className="space-y-2">
        <div className="animate-pulse rounded-xl border border-gray-100 px-4 py-2.5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-gray-100" />
              <div className="space-y-1.5">
                <div className="h-3 w-28 rounded bg-gray-100" />
                <div className="h-2.5 w-20 rounded bg-gray-100" />
              </div>
            </div>
            <div className="flex gap-6">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="space-y-1.5 text-right">
                  <div className="ml-auto h-3 w-24 rounded bg-gray-100" />
                  <div className="ml-auto h-7 w-28 rounded bg-gray-100" />
                  <div className="ml-auto h-3 w-16 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
    <div className="animate-pulse space-y-3 rounded-xl border border-gray-100 p-6">
      <div className="h-4 w-32 rounded bg-gray-100" />
      <div className="h-12 rounded-lg bg-gray-100" />
      <div className="h-12 rounded-lg bg-gray-100" />
      <div className="h-12 rounded-lg bg-gray-100" />
    </div>
  </div>
);

const CompanyBrandMark = ({
  companyName,
  domain,
  logoUrl,
  centered = false,
  compact = false,
  className = "",
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  const name = companyName || "Company";
  const initials = initialsFromName(name);
  const logoSizeClass = centered || compact ? "h-10 w-10" : "h-8 w-8";

  const logo =
    logoUrl && !failed ? (
      <img
        src={logoUrl}
        alt={name}
        className={`${logoSizeClass} shrink-0 rounded-md object-cover ring-1 ring-gray-100`}
        onError={() => setFailed(true)}
      />
    ) : (
      <div
        className={`${logoSizeClass} flex shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-600 ring-1 ring-gray-100`}
      >
        {initials}
      </div>
    );

  if (centered) {
    return (
      <div className={`flex flex-col items-center text-center ${className}`}>
        {logo}
        <p className="mt-3 text-sm font-semibold text-gray-900">{name}</p>
        {domain ? <p className="mt-0.5 text-sm text-gray-500">{domain}</p> : null}
      </div>
    );
  }

  return (
    <div className={`flex min-w-0 items-center ${compact ? "gap-3" : "gap-2.5"} ${className}`}>
      {logo}
      <div className="min-w-0 leading-none">
        <p
          className={[
            "truncate font-semibold text-gray-900",
            compact ? "text-base" : "text-sm",
          ].join(" ")}
        >
          {name}
        </p>
        {domain ? (
          <p className={["truncate text-gray-500", compact ? "mt-1 text-sm" : "text-sm"].join(" ")}>
            {domain}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const PersonAvatar = ({ pictureUrl }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [pictureUrl]);

  if (pictureUrl && !failed) {
    return (
      <img
        src={pictureUrl}
        alt=""
        referrerPolicy="no-referrer"
        className="h-9 w-9 shrink-0 rounded-full bg-gray-50 object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-100 bg-white p-1">
      <img
        src={UPGRADED_FALLBACK_LOGO}
        alt=""
        className="h-full w-full object-contain"
      />
    </div>
  );
};

const LocationBadge = ({ inCanada }) => (
  <span
    className={[
      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
      inCanada ? "bg-gray-100 text-gray-700" : "bg-amber-50 text-amber-700",
    ].join(" ")}
  >
    {inCanada ? "Canada" : "Outside Canada"}
  </span>
);

const AllocationPersonRow = ({
  person,
  savingId,
  onFieldChange,
  onDelete,
  autoFocusName = false,
  readOnly = false,
}) => {
  const [activeField, setActiveField] = useState(autoFocusName ? "name" : null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (activeField && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [activeField]);

  const href = person.linkedInWebsite || (person.email ? `mailto:${person.email}` : "");
  const isSaving = savingId === person.id;
  const allocated =
    person.allocatedRd != null
      ? person.allocatedRd
      : calculateAllocatedRd({
          salary: person.salary,
          rdPct: person.rdPct,
          employmentType: person.employmentType,
        });
  const employmentLabel =
    EMPLOYMENT_TYPES.find((option) => option.id === (person.employmentType || "employee"))
      ?.label || "Employee";

  const closeField = () => setActiveField(null);
  const openField = (field) => {
    if (!readOnly) setActiveField(field);
  };
  const cellClass = readOnly
    ? "w-full rounded-md px-2 py-1 text-left cursor-default"
    : CLICKABLE_CELL_CLASS;

  const handleFieldKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Escape") {
      event.preventDefault();
      closeField();
    }
  };

  return (
    <tr className="group align-top hover:bg-gray-50 transition-colors">
      <td className="py-2.5 pr-3 pl-0 align-middle">
        <div className="flex items-center gap-3">
          <div className="flex shrink-0 flex-col items-center gap-1">
            <PersonAvatar pictureUrl={person.pictureUrl} />
            {readOnly ? null : (
              <button
                type="button"
                onClick={() => onDelete(person)}
                className="inline-flex items-center justify-center rounded-lg p-1 text-gray-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                aria-label={`Delete ${person.name || "person"}`}
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3">
            <div className="flex min-w-0 items-center gap-1.5">
              {activeField === "name" ? (
                <input
                  ref={inputRef}
                  className={`${INLINE_INPUT_CLASS} min-w-0 max-w-full`}
                  value={person.name || ""}
                  onChange={(e) => onFieldChange(person.id, { name: e.target.value })}
                  onBlur={closeField}
                  onKeyDown={handleFieldKeyDown}
                  placeholder="Name"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => openField("name")}
                  className="min-w-0 break-words whitespace-normal rounded-md px-2 py-1 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  {person.name || "Unnamed person"}
                </button>
              )}
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                  aria-label={`Open profile for ${person.name}`}
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </a>
              ) : null}
              {isSaving ? <span className="text-xs text-gray-400">Saving…</span> : null}
            </div>
            <div className="flex min-w-0 items-center justify-end gap-2">
              <RdBadge
                isRd={person.isRd}
                onToggle={
                  readOnly
                    ? undefined
                    : () =>
                        onFieldChange(person.id, {
                          isRd: !person.isRd,
                          isRdManual: true,
                        })
                }
              />
              {activeField === "title" ? (
                <input
                  ref={inputRef}
                  className={`${INLINE_INPUT_CLASS} w-44 max-w-full`}
                  value={person.title || ""}
                  onChange={(e) => onFieldChange(person.id, { title: e.target.value })}
                  onBlur={closeField}
                  onKeyDown={handleFieldKeyDown}
                  placeholder="Title"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => openField("title")}
                  className="min-w-0 rounded-md px-2 py-1 text-left transition-colors hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <span className="block whitespace-normal text-sm text-gray-500">
                    {person.title || "Add title"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </td>

      <td className="py-2.5 px-2 align-top text-center">
        <button
          type="button"
          onClick={() => {
            if (readOnly) return;
            onFieldChange(person.id, { inCanada: !person.inCanada });
          }}
          className="inline-flex items-center justify-center rounded-md px-1 py-1 transition-colors hover:bg-gray-100"
          title={readOnly ? undefined : "Click to toggle location"}
        >
          <LocationBadge inCanada={person.inCanada} />
        </button>
      </td>

      <td className="py-2.5 px-2 align-top text-center">
        {activeField === "employmentType" ? (
          <select
            ref={inputRef}
            className={INLINE_INPUT_CLASS}
            value={person.employmentType || "employee"}
            onChange={(e) => {
              onFieldChange(person.id, { employmentType: e.target.value });
              closeField();
            }}
            onBlur={closeField}
          >
            {EMPLOYMENT_TYPES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <button
            type="button"
            onClick={() => openField("employmentType")}
            className={`${cellClass} mx-auto block w-full text-center`}
          >
            <span className="block whitespace-nowrap text-sm text-gray-600">{employmentLabel}</span>
          </button>
        )}
      </td>

      <td className="py-2.5 px-2 align-top text-center">
        {activeField === "salary" ? (
          <div>
            <input
              ref={inputRef}
              type="number"
              min="0"
              step="1000"
              className={`${INLINE_INPUT_CLASS} tabular-nums text-center`}
              value={person.salary ?? ""}
              onChange={(e) =>
                onFieldChange(person.id, {
                  salary: e.target.value === "" ? null : Number(e.target.value),
                  salarySource: "manual",
                })
              }
              onBlur={closeField}
              onKeyDown={handleFieldKeyDown}
              placeholder="—"
            />
            <p className="mt-1 text-xs text-gray-400">{salarySourceHint(person)}</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openField("salary")}
            className={`${cellClass} text-center`}
            title={salarySourceHint(person)}
          >
            <span className="text-sm font-medium tabular-nums text-gray-900">
              {formatSalary(person.salary)}
            </span>
          </button>
        )}
      </td>

      <td className="py-2.5 px-2 align-top text-center">
        {activeField === "rdPct" ? (
          <div>
            <div className="flex items-center justify-center gap-1">
              <input
                ref={inputRef}
                type="number"
                min="0"
                max="100"
                step="5"
                className={`${INLINE_INPUT_CLASS} tabular-nums text-center`}
                value={person.rdPct ?? 0}
                onChange={(e) =>
                  onFieldChange(person.id, {
                    rdPct: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                onBlur={closeField}
                onKeyDown={handleFieldKeyDown}
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <p className="mt-1 text-xs text-gray-400">{suggestRdPctHint(person.title)}</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openField("rdPct")}
            className={`${cellClass} text-center`}
            title={suggestRdPctHint(person.title)}
          >
            <span className="text-sm tabular-nums text-gray-600">{person.rdPct ?? 0}%</span>
          </button>
        )}
      </td>

      <td className="py-2.5 px-2 align-top text-center">
        <span
          className={[
            "text-sm font-semibold tabular-nums",
            allocated ? "text-emerald-700" : "text-gray-400",
          ].join(" ")}
          title={allocatedFormulaHint(person.employmentType)}
        >
          {formatSalary(allocated)}
        </span>
      </td>
    </tr>
  );
};

const TeamActionToolbar = ({
  company,
  isRefreshing,
  isPollingForTeam,
  isIndexing,
  isEstimating,
  members,
  teamSource,
  onEditTeamSource,
  inlineTeamSourceEdit,
  onRefresh,
  onIndexTeam,
  onReestimate,
  onCopyShareLink,
  shareCopied = false,
  className = "",
}) => (
  <div className={`flex flex-wrap items-center justify-end gap-2 ${className}`.trim()}>
    <HoverTooltip tip={REFRESH_TEAM_TOOLTIP}>
      <button
        type="button"
        onClick={onRefresh}
        disabled={!company?.id || isRefreshing || isPollingForTeam}
        className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Refresh team"
      >
        <ArrowPathIcon
          className={`h-4 w-4 ${isRefreshing || isPollingForTeam ? "animate-spin" : ""}`}
        />
      </button>
    </HoverTooltip>
    <TeamSourceControl
      teamSource={teamSource}
      onEdit={onEditTeamSource}
      inlineEdit={inlineTeamSourceEdit}
    />
    <HoverTooltip tip={INDEX_TEAM_TOOLTIP}>
      <button
        type="button"
        onClick={onIndexTeam}
        disabled={!company?.id || isIndexing || isPollingForTeam}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Index team"
      >
        {isIndexing ? "Indexing…" : isPollingForTeam ? "Checking…" : "Index team"}
      </button>
    </HoverTooltip>
    {onCopyShareLink ? (
      <HoverTooltip tip={SHARE_LINK_TOOLTIP}>
        <button
          type="button"
          onClick={onCopyShareLink}
          disabled={!company?.id}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <LinkIcon className="h-4 w-4" />
          {shareCopied ? "Link copied" : "Copy link"}
        </button>
      </HoverTooltip>
    ) : null}
    <HoverTooltip tip={RE_ESTIMATE_TOOLTIP}>
      <button
        type="button"
        onClick={onReestimate}
        disabled={!company?.id || isEstimating || !members.length}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Re-estimate salaries and R&D"
      >
        <SparklesIcon className={`h-4 w-4 ${isEstimating ? "animate-pulse" : ""}`} />
        {isEstimating ? "Estimating…" : "Re-estimate"}
      </button>
    </HoverTooltip>
  </div>
);

const TeamActionMessage = ({ actionMessage, compact = false }) => {
  if (!actionMessage?.text) return null;
  return (
    <div
      className={[
        compact ? "rounded-lg px-3 py-2 text-xs" : "rounded-lg px-4 py-3 text-sm",
        "break-words",
        actionMessage.type === "success" ? "bg-emerald-50 text-emerald-700" : "",
        actionMessage.type === "info" ? "bg-gray-50 text-gray-700" : "",
        actionMessage.type === "error" ? "bg-red-50 text-red-600" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {actionMessage.text}
    </div>
  );
};

const TeamSourceControl = ({ teamSource, onEdit, inlineEdit = false }) => {
  if (!teamSource?.href) {
    return (
      <div className="group relative">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-700 transition-colors hover:bg-amber-100"
          aria-label="Set LinkedIn people URL"
        >
          <LinkIcon className="h-4 w-4" />
        </button>
        <div className={HOVER_TOOLTIP_CLASS}>
          <div className="w-max max-w-xs rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm">
            <p className="text-xs leading-relaxed text-gray-600">
              {teamSource?.missingHint || "No LinkedIn people URL set."}
            </p>
            <button
              type="button"
              onClick={onEdit}
              className="mt-1.5 text-xs font-medium text-gray-900 hover:text-gray-700 transition-colors"
            >
              {inlineEdit ? "Set LinkedIn URL" : "Set in company profile"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <a
        href={teamSource.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
        aria-label={`Open team source: ${teamSource.display}`}
      >
        <LinkIcon className="h-4 w-4" />
      </a>
      <div className={HOVER_TOOLTIP_CLASS}>
        <div className="w-max max-w-sm rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm">
          <p className="break-all text-xs text-gray-600">{teamSource.display}</p>
          <button
            type="button"
            onClick={onEdit}
            className="mt-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            {inlineEdit ? "Edit URL" : "Change source"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TeamSourceEditor = ({
  teamSource,
  draft,
  onDraftChange,
  onSave,
  onCancel,
  isSaving,
}) => {
  const isLinkedIn = teamSource?.editField === "LinkedInUrl";
  const preview =
    isLinkedIn && draft.trim()
      ? buildLinkedInPeopleUrl(draft)
      : teamSource?.href || "";

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-900">
          {teamSource?.editLabel || "Team source URL"}
        </label>
        <p className="text-xs text-gray-500">
          {isLinkedIn
            ? "Clay indexes your team from the LinkedIn company page — we append /people/ automatically."
            : "Org chart URL overrides LinkedIn for team indexing."}
        </p>
      </div>
      <input
        type="url"
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        placeholder={
          isLinkedIn
            ? "https://www.linkedin.com/company/your-company"
            : "https://www.linkedin.com/company/your-company/people/"
        }
        className={`${INLINE_INPUT_CLASS} mt-3`}
      />
      {preview ? (
        <p className="mt-2 text-xs text-gray-500 break-all">
          Index team uses:{" "}
          <span className="font-medium text-gray-700">{preview.replace(/^https?:\/\//i, "")}</span>
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSaving ? "Saving…" : "Save URL"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

function CompanyTeamAllocationPanel({
  company,
  variant = "default",
  className = "",
  onCompanyUpdate,
  beforeRoster = null,
}) {
  const isOnboarding = variant === "onboarding";
  const isShare = variant === "share";
  const readOnly = isShare;
  const [shareCopied, setShareCopied] = useState(false);
  const allocation = useCompanyTeamAllocation(company, {
    inlineTeamSourceEdit: isOnboarding,
    onCompanyUpdate,
  });

  const {
    filter,
    setFilter,
    members,
    companyInCanada,
    domain,
    sharedCompany,
    isLoading,
    isRefreshing,
    isPollingForTeam,
    loadError,
    isIndexing,
    isEstimating,
    isCreating,
    savingId,
    actionMessage,
    loadMembers,
    stopTeamIndexPolling,
    handleIndexTeam,
    handleReestimate,
    handleAddPerson,
    handleFieldChange,
    handleDelete,
    allocationSummary,
    summary,
    sortedMembers,
    teamSource,
    handleEditTeamSource,
    isEditingTeamSource,
    teamSourceDraft,
    setTeamSourceDraft,
    isSavingTeamSource,
    saveTeamSource,
    cancelEditTeamSource,
    companyLogoUrl,
    saveStateLabel,
  } = allocation;

  const companyName = company?.fields?.Name || sharedCompany?.name || "";
  const showFilters = !isOnboarding;
  const showCompactStats = isOnboarding;
  const wideLayoutClass = "min-w-0 w-full";

  const rosterTitle = isOnboarding ? "Your team" : "Allocation roster";
  const rosterDescription = isShare
    ? `Estimated R&D allocation${domain ? ` for ${domain}` : ""}.`
    : isOnboarding
    ? "Matched by domain and LinkedIn. Click a value to edit."
    : `Matched by domain. ${
        companyInCanada
          ? "New people default to Canada."
          : "Set location per person."
      } Click a cell or badge to edit.`;

  const copyShareLink = async () => {
    if (typeof window === "undefined") return;
    const website = domain || company?.fields?.Domain || "";
    if (!website && !company?.id) return;
    const params = new URLSearchParams();
    if (website) {
      params.set("website", website);
      if (companyName) params.set("company", companyName);
    }
    const url = website
      ? `${window.location.origin}/rd-allocation?${params.toString()}`
      : `${window.location.origin}/rd-allocation/${company.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2000);
    } catch {
      window.prompt("Copy this link", url);
    }
  };

  const toolbarProps = {
    company,
    isRefreshing,
    isPollingForTeam,
    isIndexing,
    isEstimating,
    members,
    teamSource,
    onEditTeamSource: handleEditTeamSource,
    onRefresh: () => {
      stopTeamIndexPolling();
      loadMembers({ isManual: true });
    },
    onIndexTeam: handleIndexTeam,
    onReestimate: handleReestimate,
    onCopyShareLink: readOnly ? undefined : copyShareLink,
    shareCopied,
  };

  if (isLoading) {
    return (
      <div className={className}>
        <p className="text-sm text-gray-600">
          {isOnboarding
            ? "Loading your team…"
            : `Loading indexed team members for ${companyName || "this company"}.`}
        </p>
        <TeamLoadingState compact={isOnboarding} />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${wideLayoutClass} ${className}`}>
      {!isOnboarding && !isShare ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <TeamActionToolbar {...toolbarProps} inlineTeamSourceEdit={false} />
        </div>
      ) : null}

      {loadError ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
          <p className="font-medium">Could not load the team</p>
          <p className="mt-1 break-words">{loadError}</p>
        </div>
      ) : null}
      {!isOnboarding && !isShare ? <TeamActionMessage actionMessage={actionMessage} /> : null}

      {!isOnboarding && !isShare && isEditingTeamSource ? (
        <TeamSourceEditor
          teamSource={teamSource}
          draft={teamSourceDraft}
          onDraftChange={setTeamSourceDraft}
          onSave={saveTeamSource}
          onCancel={cancelEditTeamSource}
          isSaving={isSavingTeamSource}
        />
      ) : null}

      {showCompactStats ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="flex items-center rounded-xl border border-gray-100 bg-white px-5 py-5">
            <CompanyBrandMark
              companyName={companyName}
              domain={domain || company?.fields?.Domain}
              logoUrl={companyLogoUrl}
            />
          </div>
          <StatCard
            label="Estimated R&D payroll"
            value={formatSalary(allocationSummary.allocatedTotal)}
            hint={`${formatSalary(allocationSummary.unallocatedTotal)} unallocated · ${Math.round(
              (allocationSummary.allocatedShare || 0) * 100
            )}% of payroll allocated`}
            emphasize
          />
          <StatCard
            label="Likely R&D roles"
            value={summary.rdCount}
            hint={`${Math.round(summary.rdShare * 100)}% of team`}
          />
        </div>
      ) : (
        <AllocationStats
          allocationSummary={allocationSummary}
          companyName={companyName}
          domain={domain || company?.fields?.Domain}
          logoUrl={companyLogoUrl}
        />
      )}

      {beforeRoster}

      <div
        className="rounded-xl border border-gray-100 bg-white p-5 sm:p-6"
      >
        <div className="mb-5 space-y-3">
          {isOnboarding ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-start gap-2">
                <UserGroupIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900">{rosterTitle}</h4>
                  <p className="mt-0.5 text-sm text-gray-500">{rosterDescription}</p>
                </div>
              </div>
              <TeamActionToolbar {...toolbarProps} inlineTeamSourceEdit className="shrink-0" />
            </div>
          ) : (
            <div className="grid grid-cols-1 items-center gap-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
              <div className="flex min-w-0 items-start gap-2">
                <UserGroupIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900">{rosterTitle}</h4>
                  <p className="mt-0.5 text-sm text-gray-500">{rosterDescription}</p>
                </div>
              </div>
              <TeamBreakdownRow
                canadaCount={allocationSummary.canadaCount}
                memberCount={members.length}
                employeeCount={allocationSummary.employeeCount}
                contractorCount={allocationSummary.contractorCount}
                rdCount={summary.rdCount}
                rdShare={summary.rdShare}
              />
              {showFilters ? (
                <div className="flex justify-end">
                  <div className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-white p-1">
                    {FILTERS.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setFilter(item.id)}
                        className={[
                          "whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                          filter === item.id
                            ? "bg-gray-900 text-white"
                            : "text-gray-600 hover:text-gray-900",
                        ].join(" ")}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {isOnboarding && isEditingTeamSource ? (
            <TeamSourceEditor
              teamSource={teamSource}
              draft={teamSourceDraft}
              onDraftChange={setTeamSourceDraft}
              onSave={saveTeamSource}
              onCancel={cancelEditTeamSource}
              isSaving={isSavingTeamSource}
            />
          ) : null}

          {isOnboarding ? <TeamActionMessage actionMessage={actionMessage} compact /> : null}

          {readOnly ? null : (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {saveStateLabel !== "All changes saved" ? (
                <span className="shrink-0 text-sm text-gray-400">{saveStateLabel}</span>
              ) : null}
              <button
                type="button"
                onClick={handleAddPerson}
                disabled={!company?.id || isCreating}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" />
                {isCreating ? "Adding…" : "Add person"}
              </button>
            </div>
          )}
        </div>

        {!sortedMembers.length ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <CompanyBrandMark
              companyName={companyName}
              domain={domain || company?.fields?.Domain}
              logoUrl={companyLogoUrl}
              centered
            />
            <p className="mt-4 text-sm font-medium text-gray-900">
              {members.length
                ? "No people in this view"
                : `No team indexed for ${companyName || "this company"} yet`}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {members.length
                ? "Try switching the filter above."
                : isShare
                ? "This link will show people once the team is indexed."
                : isOnboarding
                ? `Index your team from ${
                    domain || company?.fields?.Domain || "your company domain"
                  }, or add people manually below.`
                : `Index the team from ${
                    domain || company?.fields?.Domain || "the company domain"
                  }, or add people manually.`}
            </p>
            {!members.length && !readOnly ? (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={handleAddPerson}
                  disabled={!company?.id || isCreating}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <PlusIcon className="h-4 w-4" />
                  {isCreating ? "Adding…" : "Add person"}
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className={`${TABLE_HEAD_CLASS} pl-0 w-[52%]`}>Person</th>
                  <th className={`${TABLE_HEAD_CLASS} w-[8%]`}>Location</th>
                  <th className={`${TABLE_HEAD_CLASS} w-[10%]`}>Type</th>
                  <th className={`${TABLE_HEAD_CLASS} w-[12%]`}>Market salary</th>
                  <th className={`${TABLE_HEAD_CLASS} w-[8%]`}>% R&D</th>
                  <th className={`${TABLE_HEAD_CLASS} pr-3 w-[10%]`}>Allocated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sortedMembers.map((person) => (
                  <AllocationPersonRow
                    key={person.id}
                    person={person}
                    savingId={savingId}
                    onFieldChange={handleFieldChange}
                    onDelete={handleDelete}
                    readOnly={readOnly}
                    autoFocusName={!readOnly && person.name === "New teammate" && !person.title}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td className="pt-4 pl-0 text-sm font-medium text-gray-900" colSpan={5}>
                    Allocation total (visible rows)
                  </td>
                  <td className="pt-4 px-2 text-center text-sm font-semibold tabular-nums text-emerald-700">
                    {formatSalary(
                      sortedMembers.reduce((sum, m) => {
                        const amount =
                          m.allocatedRd != null
                            ? m.allocatedRd
                            : calculateAllocatedRd({
                                salary: m.salary,
                                rdPct: m.rdPct,
                                employmentType: m.employmentType,
                              });
                        return sum + (amount || 0);
                      }, 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyTeamAllocationPanel;
